/**
 * Hook to fetch custom field definitions for companies/accounts from CRM ingestion metadata
 *
 * This is the proper way to get custom field definitions - query the field metadata
 * from the backend rather than iterating through account records.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  useGetCrmIngestionFieldsLazyQuery,
  CrmIngestionPlatform,
  CrmIngestionObjectType,
} from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { usePreviewCustomFieldsFor, mergeCustomFieldLists } from '@/lib/hooks/use-preview-custom-fields';
import {
  customFieldsFromCrmIngestion,
  standardFieldsFromCrmIngestion,
  type CustomFieldDefinition,
} from '@/lib/utils/crm-custom-fields';

export type { CustomFieldDefinition };

export const useCompanyCustomFields = () => {
  const workspaceId = useWorkspaceId();
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);
  const previewFields = usePreviewCustomFieldsFor('Account');
  // True once the definitions query has settled (data or error), i.e. `customFields`
  // reflects the backend rather than the initial []. Set in the same effect that
  // sets customFields so consumers never observe hydrated=true with stale fields.
  // Lets consumers (grid summaries) wait for the final column set instead of
  // firing once for standard columns and again when custom columns arrive.
  const [hydrated, setHydrated] = useState(false);

  const [getCrmIngestionFields, { data, loading, error }] =
    useGetCrmIngestionFieldsLazyQuery({
      // cache-first: see use-contact-custom-fields.ts — mutations that change
      // field definitions refresh this operation explicitly, so consumers can
      // share the cached result instead of duplicating the network call.
      fetchPolicy: 'cache-first',
    });

  useEffect(() => {
    if (workspaceId) {
      getCrmIngestionFields({
        variables: {
          workspaceId: parseInt(workspaceId),
          platform: CrmIngestionPlatform.Coffee, // ⚠️ Use COFFEE (not Salesforce/HubSpot)
          first: 100,
        },
      });
    }
  }, [workspaceId, getCrmIngestionFields]);

  useEffect(() => {
    if (data?.crmIngestionFields?.edges) {
      // Shared pure mapping (also used by the idle prefetch) — ⚠️ ACCOUNT not CONTACT
      setCustomFields(customFieldsFromCrmIngestion(data, CrmIngestionObjectType.Account));
    }
    // Error also hydrates: consumers should proceed with standard columns rather
    // than wait forever for definitions that will never arrive.
    if (data || error) setHydrated(true);
  }, [data, error]);

  // Memoize the merge so consumers get a stable array reference across renders
  // (fresh array every render would defeat downstream memoization; this hook
  // feeds the Companies grid and the Company detail sheet).
  const mergedCustomFields = useMemo(
    () => mergeCustomFieldLists(customFields, previewFields as CustomFieldDefinition[]),
    [customFields, previewFields],
  );

  // Standard (non-custom) field definitions for this object. Formula fields
  // reference them by `crmName`, and the backend validates references against
  // the very same rows, so this list is the safe vocabulary to offer.
  const standardFields = useMemo(
    () => standardFieldsFromCrmIngestion(data, CrmIngestionObjectType.Account),
    [data],
  );

  return {
    customFields: mergedCustomFields,
    standardFields,
    loading,
    error,
    hydrated,
  };
};
